import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const DEFAULT_SITE_CONTENT = {
  nav: {
    brand: "Greg Pryor",
    items: [
      { id: "hero", label: "Home", index: "00" },
      { id: "booth", label: "Live Booth", index: "01" },
      { id: "book", label: "The Book", index: "02" },
      { id: "about", label: "About Greg", index: "03" },
      { id: "memorabilia", label: "The Collection", index: "04" },
      { id: "gallery", label: "Gallery", index: "05" },
      { id: "watch", label: "Game Tape", index: "06" },
      { id: "podcasts", label: "On the Air", index: "07" },
      { id: "speaking", label: "On the Road", index: "08" },
      { id: "apply", label: "Sit Next To Me", index: "09" },
      { id: "contact", label: "Get in Touch", index: "10" },
    ],
  },
  hero: {
    badge: "№ 4 · MLB INFIELDER · 1976–1986",
    firstName: "Greg",
    lastName: "Pryor",
    lede: "Former Major Leaguer. World Series Champion. Author. Speaker. I have stories. I'm finally telling them.",
    sublede: "Forty years later, the dugout is quieter, but the memory is louder. Pull up a seat.",
    primaryCta: "Read the book",
    secondaryCta: "Sit next to me",
    stats: [
      { value: "10", label: "MLB SEASONS" },
      { value: "789", label: "GAMES" },
      { value: "6", label: "YEARS IN THE MINORS" },
      { value: "1", label: "WORLD SERIES RING" },
    ],
  },
  // Greg's live booth on the home page (src/components/booth.jsx). The
  // schedule, the countdown and the live picture come from the booth API;
  // this is the copy around them.
  booth: {
    eyebrow: "Greg's live booth",
    titlePrefix: "The big games,",
    titleEmphasis: "called live",
    titleSuffix: "from Greg's living room.",
    lede: "Put the game on your TV and Greg on your phone. He calls it pitch by pitch, tells you the stories behind the plays, reads your questions on the air and shows you what's on the shelf - the ring included, if you ask nice.",
    note: "Greg goes live about 15 minutes before first pitch and stays through the last out.",
    watchTitle: "Watch free, nothing to install",
    watchText: "Phone, tablet or laptop, no sign-up. Open the page and you're in the booth.",
    askTitle: "Ask him anything",
    askText: "Send a question from the page and he reads it on the air, in the order they come in.",
    alertsTitle: "Never miss a night",
    alertsText: "One email when he goes live, nothing else. One click to stop.",
  },
  book: {
    eyebrow: "The Book",
    titleBefore: "A clubhouse seat to",
    titleEmphasis: "baseball's strangest decade",
    lede: "In 1977 I was a 27-year-old career minor leaguer with a Yankees contract, a mustache, and a dwindling supply of optimism. Then George Steinbrenner walked through the clubhouse with a razor in his hand. This is what happened next - and the ten years that followed.",
    pullQuote:
      "Greg was one of my all-time favorite teammates. During games he was a hard-nosed player and after the game he was one of the funniest guys around. What a treat it is to be able to call him a teammate.",
    pullAttribution: "George Brett, HOF, Royals 3B",
    directTabLabel: "Direct from Greg",
    directTabSub: "Signed · $28.95 · Greg's pick",
    directBadge: "Recommended",
    amazonTabLabel: "Amazon",
    amazonTabSub: "Kindle only",
    directCta: "Order signed copy",
    amazonCta: "Open Amazon listing",
    quickBuyCta: "Buy the book",
    directNote:
      "Signed in real ink. Packed in Greg's garage. Shipped from Olathe in about a week. Your $28.95 goes to Greg, not Jeff Bezos — and he writes the inscription however you want it.",
    amazonNote:
      "Best if you want the Kindle version. For a physical copy, the signed one ships just about as fast — and Greg actually sees the order come through.",
    teaserTag: "Book 2",
    teaserDate: "Coming in 2027",
    teaserText: "27 new chapters, including the never-before-told story of...",
    quotes: [
      {
        who: "Tony La Russa",
        role: "White Sox manager, HOF 2014",
        text: "Greg was a key part of our White Sox family. Sincerity in your words and actions is a must. That's why my favorite words in his book spoke to his sincerity. “I loved being nervous before games because it helped me get mentally prepared.” Unless you care, you don't get nervous. Greg always cared.",
      },
      {
        who: "John Schuerholz",
        role: "Royals GM, HOF 2017",
        text: "Reflecting back specially to our acquiring Greg from the White Sox in 1982 and the importance we placed on adding not only a player who filled our needs, but as well added to the character and determination of our team, he was certainly that type of player and competitor during his five seasons as a KC Royal.",
      },
    ],
  },
  checkout: {
    headerTag: "DIRECT FROM GREG · NO. 04",
    headerTitle: "Order signed copy",
    editionHeading: "Pick your edition.",
    editionLede:
      "Two ways to take a book home from the dugout. Signed by me, personalized however you want.",
    quantityLabel: "How many copies?",
    quantityHelp: "Buying for the whole team? Greg ships up to 25 in one box.",
    addonTag: "ADD A SIGNED BALL · +$55",
    addonName: "Official MLB ball, signed by Greg",
    addonSub: "Personalize it too. Ships protected in a UV display case.",
    personalizeHeading: "Make it yours.",
    personalizeLedeSingle:
      "Tell me who I'm writing this for. I'll handle the rest with a real pen.",
    personalizeLedeMultiple:
      "Tell me who I'm writing this for. All {quantity} copies get the same inscription - message me after if you need different ones per copy.",
    unsignedNote:
      "You picked the unsigned softcover, so there's nothing to personalize on the book itself.",
    shippingHeading: "Where am I sending it?",
    shippingLede: "Drops in the mail from Olathe, Kansas. I write the address by hand.",
    paymentHeading: "Payment",
    paymentLede:
      "Card data goes directly to Stripe - Greg never sees it. Apple Pay and Google Pay appear here when supported.",
    editions: {
      standard: { name: "Softcover, unsigned", sub: "Direct from Greg's garage. Ships in 2 days." },
      signed: {
        name: "Softcover, signed by Greg",
        sub: "Personalized inscription available. Ships in a week.",
      },
    },
  },
  memorabilia: {
    eyebrow: "The Collection",
    titlePrefix: "The",
    titleEmphasis: "things",
    titleSuffix: "I never threw out.",
    lede: "Some of these I earned. Some I borrowed. One of them I'm pretty sure still belongs to Hal McRae. A small museum of a ten-year career.",
    items: [
      {
        id: 1,
        title: "1985 World Series Trophy",
        category: "Championship",
        year: "1985",
        desc: "The replica every player on the '85 Royals got - pennants for all 26 clubs, the crown on top, our names engraved on the base. Mine sits on a bookshelf in the den. Forty-one years and the gold still hasn't dulled. Some nights I just walk past it and nod.",
      },
      {
        id: 2,
        title: "1985 Royals Championship Ring",
        category: "Championship",
        year: "1985",
        desc: "PRYOR across the top, the Royals crown shield in the middle, my number 4 underneath. I wore it to a card show in 1991 and a kid asked if it was real. I told him it was the most real thing I owned.",
      },
      {
        id: 3,
        title: "Signed Baseball Display",
        category: "Signed",
        year: "Various",
        desc: "Twelve baseballs, twelve stories. Mickey Mantle. Bob Gibson. Willie Mays. George Brett with W.S. M.V.P. under his name. Two from my own first big league home runs - both in the same game off Seattle at Comiskey, September '78. The case stays locked. I have the only key.",
      },
      {
        id: 4,
        title: "1984 AL West Champions Plaque",
        category: "Award",
        year: "1985",
        desc: "Handed out at the 14th Annual Kansas City Baseball Awards Dinner, January 26th, 1985. \"In recognition of outstanding individual achievements and contributions.\" I played 123 games that year. They were generous with the word 'outstanding.'",
      },
      {
        id: 5,
        title: "Team-Signed Moët",
        category: "Signed",
        year: "1985",
        desc: "Brut Impérial. Signed by the boys after we clinched. I never opened it. My wife asks every anniversary. The answer is still no.",
      },
      {
        id: 6,
        title: "Game-Worn Pony Cleats",
        category: "Gear",
        year: "1984",
        desc: "Made in USA. Two-tone blue, busted at the toe, the Pony tongue flap nearly torn off the right one. I wore these the season we won the West. They smell like a dugout in August. I've never had the heart to clean them.",
      },
      {
        id: 7,
        title: "Champions Weekend 2015",
        category: "Memento",
        year: "2015",
        desc: "Engraved bottle from the Royals' Champions Weekend - the '85 club back together to celebrate the 2015 ring. Thirty years between titles. We were grayer. The hugs were the same.",
      },
      {
        id: 8,
        title: "Comiskey Souvenir Baseball",
        category: "Memento",
        year: "1979",
        desc: "A printed-cover ball with the old ballpark on it - light towers, packed grandstand, the South Side in full roar. White Sox days. A reminder that before Kansas City, there was Comiskey, and that mattered too.",
      },
      {
        id: 9,
        title: "Norman Rockwell Figurine",
        category: "Memento",
        year: "1986",
        desc: "Given to me by my dad after the World Series. Rockwell's umpire, the kids gathered around him at home plate. Dad said it reminded him of me at nine years old. I keep it on the windowsill where the morning light hits it.",
      },
      {
        id: 10,
        title: "1912 Duffy, Ohio Team",
        category: "Memento",
        year: "1912",
        desc: "This is a picture of a 1912 semi-pro team in Duffy, Ohio. My grandpa Homer Pryor is in the back row, second from the left. What are the odds of having a grandpa named Homer and also hitting homers as a major leaguer?",
      },
      {
        id: 11,
        title: "1971 First Pro Contract",
        category: "Document",
        year: "1971",
        desc: "The first time anybody paid me to play. Geneva of the New York-Penn League, signed June 21st, 1971 - five hundred dollars a month, six thousand to sign, and another four if I stuck around. Felt like the whole world to a kid from Orlando. A Washington Senators farm contract, the very bottom rung of the ladder. Six years of bus rides in the minors started right here. I never threw it out.",
      },
      {
        id: 12,
        title: "The Senators Welcome Letter",
        category: "Document",
        year: "1971",
        desc: "Four days after I signed, this landed in the mailbox up in Geneva. Washington Senators letterhead, Robert F. Kennedy Stadium printed across the top, and a hearty welcome from Hal Keller, the farm director. He wished me 'success in a career with baseball.' A career - and I hadn't played a single inning of pro ball yet. The Senators packed up for Texas the very next year. I kept the letter anyway.",
      },
      {
        id: 13,
        title: "1974 Big-League Camp Invite",
        category: "Document",
        year: "1974",
        desc: "My first invitation to a big-league camp. February 1974, Texas Rangers letterhead with that cowboy-hat logo - the old Senators had become the Rangers by then. 'It gives me great pleasure on behalf of Billy Martin to invite you to the Major League spring training camp.' Pompano Beach, report February 21st, in top physical condition. They even asked the married fellas to leave the wives at home. Three years of bus rides and a big-league staff finally wanted a look. I about wore the envelope out reading it.",
      },
      {
        id: 14,
        title: "1975 Big-League Camp Invite",
        category: "Document",
        year: "1975",
        desc: "And then they asked me back. February 1975, same Rangers letterhead, same Pompano Beach, another letter from Billy Martin tucked inside. Daniel O'Brien signed this one too - vice president when he wrote me in '74, general manager by '75. 'Only signed players will be permitted in camp.' Two springs in a row getting a look from a big-league staff. In 1976 I finally made it to the majors. You hang on to the letters that got you there.",
      },
    ],
  },
  gallery: {
    eyebrow: "The Gallery",
    titlePrefix: "The",
    titleEmphasis: "photo",
    titleSuffix: "album.",
    lede: "Photos, clippings, and a few things worth keeping - from sixteen years in the game and the years since. Tap any one to see it full size.",
    items: [
      {
        id: 1,
        title: "Royal Finish",
        category: "Championship",
        year: "1985",
        desc: "A signed, numbered lithograph of the pile at the mound the instant the Royals won Game 7 of the 1985 World Series. That's No. 4 - Pryor - in the middle of it, with McRae, Wilson, and Saberhagen.",
      },
      {
        id: 2,
        title: "Game 7, the last out",
        category: "Championship",
        year: "1985",
        desc: "October 27, 1985. The dogpile starts to form as the final out settles in - Sheridan, Pryor, McRae, Smith, and Orta all converging on the mound.",
      },
      {
        id: 3,
        title: "The Royals at the White House",
        category: "Champions",
        year: "1985",
        desc: "President Reagan walks past the 1985 World Champion Royals in the Rose Garden after congratulating the club - clipped straight out of the next day's paper.",
      },
      {
        id: 4,
        title: "Signed by Brett",
        category: "Clubhouse",
        year: "1985",
        desc: "The clubhouse after clinching, somebody emptying a Budweiser over a teammate's head. George Brett signed it across the bottom in blue.",
      },
      {
        id: 31,
        title: "Two of the boys",
        category: "Clubhouse",
        year: "—",
        desc: "Another shot from the clinch — a teammate tips a full Budweiser over the heads of two Royals as they hang onto each other, laughing. Powder-blue road top on one, a soaked undershirt on the other, and not a dry collar in the clubhouse.",
      },
      {
        id: 32,
        title: "Rocky Asked for Mine",
        category: "Video",
        year: "1982",
        desc: "Greg tells it himself. As a kid he stood in a line around the block at Polsky's department store in Akron for the autograph of his hero, Rocky Colavito. In 1982, his first season with the Royals, Colavito was the club's hitting coach, and he walked up to Greg's locker with two baseballs for him to sign.",
      },
      {
        id: 5,
        title: "Game 6 ticket",
        category: "Championship",
        year: "1985",
        desc: "Field box seat, forty dollars, Game 6 of the 1985 World Series at Royals Stadium - the night that kept the whole thing alive.",
      },
      {
        id: 6,
        title: "The official ball",
        category: "Championship",
        year: "1985",
        desc: "The official Rawlings ball of the 1985 World Series, Commissioner Peter Ueberroth's signature stamped across the sweet spot.",
      },
      {
        id: 7,
        title: "World Series 1985",
        category: "Insignia",
        year: "1985",
        desc: "The official logo of the Fall Classic that ended with a crown in Kansas City.",
      },
      {
        id: 8,
        title: "Crowns in the stands",
        category: "Fans",
        year: "—",
        desc: "Gold crowns and a 1985 World Champions tee out in the seats. The blue never left Kansas City.",
      },
      {
        id: 9,
        title: "The victory parade",
        category: "Champions",
        year: "—",
        desc: "Ticker tape, slow convertibles, and a street packed shoulder to shoulder for a championship parade.",
      },
      {
        id: 10,
        title: "World Series cap",
        category: "Family",
        year: "—",
        desc: "A souvenir Series cap and a kid along for the ride through the crowd.",
      },
      {
        id: 11,
        title: "Chicago White Sox",
        category: "White Sox",
        year: "1978–81",
        desc: "On a knee in the white-and-navy pullovers. Bill Veeck gave Greg his first real big-league shot with the Sox in 1978.",
      },
      {
        id: 12,
        title: "Texas Rangers",
        category: "Texas",
        year: "1970s",
        desc: "In the Rangers' road grays. The old Washington club that drafted Greg had become Texas by the time he reached the majors in 1976.",
      },
      {
        id: 13,
        title: "Bus-league days",
        category: "The Minors",
        year: "1970s",
        desc: "Six years in the minors before the big leagues stuck - a young infielder waiting his turn at the cage.",
      },
      {
        id: 14,
        title: "Before baseball",
        category: "Off the Field",
        year: "—",
        desc: "A three-point stance and a leather helmet, back before the diamond won out.",
      },
      {
        id: 15,
        title: "Home whites and family",
        category: "Family",
        year: "—",
        desc: "In the Royals uniform with family - the part of the job that never showed up in a box score.",
      },
      {
        id: 16,
        title: "Back at the ballpark",
        category: "Then & Now",
        year: "—",
        desc: "Still showing up at the yard, all these years later, with an old friend and a grin.",
      },
      {
        id: 17,
        title: '"Pryor: Heir Apparent?"',
        category: "In Print",
        year: "1977",
        desc: "A sports column once ran this verse under Greg's own byline - his poem about clawing onto the roster out of Rangers camp in Pompano, spring of '77.",
      },
      {
        id: 18,
        title: "The back cover",
        category: "The Book",
        year: "—",
        desc: "The back of The Day the Yankees Made Me Shave - twenty-seven days, sixteen years, and praise from Brett, La Russa, and Schuerholz.",
      },
      {
        id: 19,
        title: "George Brett, in ink",
        category: "Teammates",
        year: "—",
        desc: "A pen-and-ink tribute to Greg's Hall of Fame teammate - the whole Royals career drawn into the margins.",
      },
      {
        id: 20,
        title: "The Pine Tar ruling",
        category: "Pine Tar",
        year: "1983",
        desc: "The AP wire copy of A.L. President Lee MacPhail's decision on the Pine Tar Game - Brett's home run, the rulebook, and the protest that stuck.",
      },
      {
        id: 21,
        title: "The Pine Tar Game",
        category: "Pine Tar",
        year: "1983",
        desc: "July 24, 1983, Yankee Stadium - players and umpires in the thick of the decade's most famous at-bat. Greg was on that Royals club.",
      },
      {
        id: 22,
        title: "Washington Senators bag",
        category: "Origins",
        year: "1971",
        desc: "An old canvas equipment bag from the Senators, No. 24 - the organization that drafted Greg in 1971, a year before it packed up for Texas.",
      },
      {
        id: 23,
        title: "Marvin Miller",
        category: "The Game",
        year: "—",
        desc: "The union leader who changed the business of the game for every player who came after - Greg included.",
      },
      {
        id: 24,
        title: "Disco Demolition Night",
        category: "White Sox",
        year: "1979",
        desc: "July 12, 1979, Comiskey Park. The promotion that ended with fans on the field and the second game forfeited. Greg's White Sox were the home team.",
      },
      {
        id: 25,
        title: "Old National League squad",
        category: "History",
        year: "—",
        desc: "A vintage National League team posed at the cage, flannels and all.",
      },
      {
        id: 26,
        title: "Team portrait",
        category: "Team Photo",
        year: "—",
        desc: "A full club photo in Phillies road grays.",
      },
      {
        id: 27,
        title: "Black tie",
        category: "Off the Field",
        year: "—",
        desc: "Tuxedos and a head table - a night on the baseball banquet circuit.",
      },
      {
        id: 28,
        title: "Norman Rockwell",
        category: "Art",
        year: "—",
        desc: "A Norman Rockwell baseball print - the game the way it lives in the imagination.",
      },
      {
        id: 29,
        title: "Too much baseball",
        category: "Funny Pages",
        year: "—",
        desc: "Charlie Brown and Franklin put a finger on a problem every ballplayer knows.",
      },
      {
        id: 30,
        title: "Long, lonely winter",
        category: "Funny Pages",
        year: "—",
        desc: "Snow-blowing a diamond into the backyard, waiting on spring. Every fan in February.",
      },
    ],
  },
  watch: {
    eyebrow: "Roll the Tape",
    titlePrefix: "Watch me",
    titleEmphasis: "play.",
    lede: "Most of what I did lives in box scores and the back of a baseball card. But the tape still rolls. Start with the highlight reel — the full 1980 broadcast's right underneath, and the rest is being dug up as we find it.",
    ondeckNote: "Still digitizing the archive",
  },
  about: {
    eyebrow: "About Greg",
    titleLine1: "Ten seasons.",
    titleLine2: "One ring",
    titleLine3: "A lot of vinyl.",
    bio1: "Born in Marietta, Ohio. Drafted by the Senators in the 6th round in 1971. Spent six years in the minors before Bill Veeck gave me a real shot with the White Sox in 1978. Made the majors for good at 28. Played 789 big league games at second, short, and third. Hit .250 lifetime. Ring in '85.",
    bio2: "Being a utility player meant I had to be sharper than everyone else - ready to step in at four positions on a moment's notice, and good enough to be trusted in October. It's the position that keeps a clubhouse together and a contender on the field. I wore the role with pride, and I came home with the ring to show for it.",
    careerHeading: "Career",
  },
  speaking: {
    heading: "On the Road",
    subheading: "SPEAKING · STORYTELLING · CLINICS",
    eyebrow: "BOOK A SPEAKING ENGAGEMENT",
    headlineLine1: "Bring me to your",
    headlineEmphasis: "special event",
    headlineLine2: "your sales clubhouse,",
    headlineLine3: "or your kid's little league camp.",
    lede: "16 years in the game taught me a lot of character - and a few lessons worth passing on. I do corporate keynotes, fundraisers, fantasy camps, school visits, and the occasional birthday party if the cake is good. The Yankees made me shave. They didn't make me boring.",
    list: [
      {
        title: "Keynotes & Banquets",
        text: "30-60 min. Stories from the dugout, the bus, and the World Series.",
      },
      {
        title: "Youth Clinics",
        text: "Glove work, base running, and what to do when nobody's watching.",
      },
      { title: "Fundraisers & Q&A", text: "Bring the ring. Sign the book. Stay for dinner." },
    ],
    cta: "Request a date",
    note: "WILL TRAVEL FOR THE RIGHT BBQ",
  },
  apply: {
    eyebrow: "Sit Next To Me",
    title: "Sit",
    titleEmphasis: "next to me",
    lede: "I have three Diamond Club seats at Kauffman and I'm at every home game. The seat in the middle is mine. The two on either side are yours, if you want them. Bring a friend. Bring your dad. Bring whoever you want to spend nine innings hearing stories with.",
    bullets: [
      "Two seats next to mine. Diamond Club Box, Row 2, seats 1-3.",
      "I'll be there too - stories, scorekeeping, peanuts.",
      "Tell me why you want to come. Be honest.",
      "No money changes hands. Just baseball.",
    ],
    calloutTag: "DIAMOND CLUB BOX · ROW 2 · SEATS 1-3",
    calloutText: "Three seats, one of them is mine. The other two are why you're here.",
    // "open" renders the application form; "closed" keeps the section on the
    // page (the pitch, the seats, the callout) but swaps the form for the
    // off-season card below. Flip it back to "open" and add next season's
    // home dates to `upcomingGames` when the Royals publish the schedule.
    status: "closed",
    formTag: "APPLICATION · 2027 SEASON",
    finePrint: "I read every one. No bots. No bots. No bots.",
    successEyebrow: "You're In",
    successTitle: "See you at the",
    successTitleEmphasis: "K",
    successText:
      "I read every application personally. If we're a fit, I'll email you within a week with confirmation, parking instructions, and a meet-up spot before first pitch. The seats are next to mine - so come ready to talk baseball for nine innings.",
    // Home dates for the open form. Empty between seasons; with no dates the
    // form falls back to a free-text "which game?" field so it never dead-ends.
    upcomingGames: [],
    closed: {
      tag: "APPLICATIONS · CLOSED FOR THE SEASON",
      title: "See you in",
      titleEmphasis: "2027",
      text: "The 2026 season is in the books, and so are this year's seats. Applications open again in the spring, once the Royals put out next year's home schedule. Same three seats. Same deal. No money changes hands.",
      note: "PITCHERS AND CATCHERS REPORT IN FEBRUARY · THE FORM REOPENS RIGHT AFTER",
    },
  },
  mytake: {
    eyebrow: "My Take",
    titleLine1: "I have",
    titleEmphasis: "opinions",
    titleLine2: "about your baseball.",
    lede: "The game I came up in is not the game on the field today. Some of that's good. Some of it's not. Here's where I land.",
    weeklyTag: "WORLD SERIES WEEK",
    weeklyFoot: "- Greg, in his recliner, with a drink",
    weekly: {
      date: "October 23, 2026",
      team: "Rays vs. Brewers",
      record: "World Series, Game 1 tonight",
      take: "Two weeks ago I gave you Milwaukee over the Dodgers and Tampa Bay out of the American League. The Brewers swept Los Angeles in four and the Rays put Cleveland away in six, so I'm two for two, and I'm not changing my pick now: Brewers in six. Here's the case. Milwaukee won 103 games, the most in baseball, and they've lost exactly one game this October. That's a club that plays catch, runs the bases right and doesn't beat itself - I've said it before, that's not a hot take, that's 1985. Tampa Bay won 98 and swept the Yankees, which I enjoyed from the recliner. The two Championship Series MVPs tell you what kind of matchup this is: the Rays' Freddy Peralta pitched eight years in Milwaukee before they traded him last January, and the Brewers' Jake Bauers broke in with Tampa Bay. Small town, baseball. Neither franchise has ever won it. Milwaukee lost Game 7 to the Cardinals in 1982, back when they were in our league and I was in the Royals' dugout - I hit .310 against Milwaukee in my career, 44 hits in 57 games, and I'm sure somebody there remembers. Tampa Bay lost in 2008 and 2020. Somebody gets their first ring next week. I'll be in the booth for every pitch, starting tonight at 8 Eastern, and they've got Misiorowski listed against Nick Martinez. Pull up a seat.",
    },
    takes: [
      {
        id: 5,
        hot: true,
        topic: "ABS / Robo Umps",
        verdict: "Long overdue.",
        body: "If a pitcher paints the corner, he ought to get the call - not lose it because the catcher framed it poorly or the ump's eyes are tired in the eighth. ABS rewards the guys who can actually pitch. I'm in.",
        reactions: 521,
        comments: 203,
      },
      {
        id: 1,
        hot: true,
        topic: "The Ghost Runner",
        verdict: "Bench it.",
        body: "The whole point of extra innings was that you earned the runner. Now we just hand them one and call it a game. Statistical fiction. Get rid of it.",
        reactions: 312,
        comments: 87,
      },
      {
        id: 2,
        hot: false,
        topic: "The Pitch Clock",
        verdict: "Keep it.",
        body: "I came up in an era where Mike Hargrove took 90 seconds between pitches. Nobody misses it. Game's crisper. I'm in.",
        reactions: 198,
        comments: 41,
      },
      {
        id: 3,
        hot: true,
        topic: "The Banned Shift",
        verdict: "Bring it back.",
        body: "If you can't pull a ground ball through a defender, that's on you. Banning the shift is babying hitters. Adapt or get demoted.",
        reactions: 412,
        comments: 156,
      },
      {
        id: 4,
        hot: false,
        topic: "Bigger Bases",
        verdict: "I'll allow it.",
        body: "Three more inches. Stolen bases are up. Game looks faster. I don't see the controversy here.",
        reactions: 89,
        comments: 22,
      },
    ],
  },
  health: {
    eyebrow: "ALSO RUNNING - SINCE 1991",
    titleLine1: "Played the long game.",
    titleEmphasis: "Still playing it.",
    lede: "After baseball, I co-founded Life Priority with my wife. Our flagship product, LIFT Caps, is a science-first energy and focus formula - caffeine paired with L-Phenylalanine, B-vitamins, and choline. No crash, no jitters, just a clean lift that keeps you moving.",
    pillars: ["Clean energy, no crash", "Mental focus & clarity", "Mood & motivation"],
    cta: "Shop Life Priority",
    yearsLabel: "YEARS IN BUSINESS",
  },
  facebook: {
    eyebrow: "FROM THE FACEBOOK PAGE",
    titlePrefix: "Latest from",
    titleHandle: "@GregPryor85",
    followCta: "Follow on Facebook",
    bottomCta: "Follow @GregPryor85 - the stories don't stop",
  },
  contact: {
    tag: "GET IN TOUCH",
    headingLine1: "I read everything.",
    headingEmphasis: "Eventually.",
    lede: "Press, interviews, autograph requests, baseball questions, or a friendly hello - pick a button.",
    emailCta: "Email Greg",
    facebookCta: "Facebook",
    pressCta: "Press Kit",
    autographCta: "Autograph Request",
  },
  footer: {
    brand: "Greg Pryor",
    subbrand: "№ 4 · ROYALS '85",
    quote:
      '"I was axed three times by Billy Martin in the minors. In \'78, Bill Veeck gave me a job. The rest is in the book."',
    copyright: "© 2026 GREG PRYOR. ALL RIGHTS RESERVED.",
    credit: "DESIGNED IN KANSAS CITY · BUILT WITH LOVE FOR THE GAME",
  },
};

export const CONTENT_SECTIONS = [
  {
    id: "hero",
    label: "Home",
    fields: [
      ["hero.badge", "Badge"],
      ["hero.firstName", "First name"],
      ["hero.lastName", "Last name"],
      ["hero.lede", "Main intro", "textarea"],
      ["hero.sublede", "Second intro", "textarea"],
      ["hero.primaryCta", "Primary button"],
      ["hero.secondaryCta", "Secondary button"],
      ["hero.stats.0.value", "Stat 1 value"],
      ["hero.stats.0.label", "Stat 1 label"],
      ["hero.stats.1.value", "Stat 2 value"],
      ["hero.stats.1.label", "Stat 2 label"],
      ["hero.stats.2.value", "Stat 3 value"],
      ["hero.stats.2.label", "Stat 3 label"],
      ["hero.stats.3.value", "Stat 4 value"],
      ["hero.stats.3.label", "Stat 4 label"],
    ],
  },
  {
    id: "booth",
    label: "Live Booth",
    fields: [
      ["booth.eyebrow", "Eyebrow"],
      ["booth.titlePrefix", "Title prefix"],
      ["booth.titleEmphasis", "Title emphasis"],
      ["booth.titleSuffix", "Title suffix"],
      ["booth.lede", "Intro", "textarea"],
      ["booth.note", "Note under the schedule"],
      ["booth.watchTitle", "Watch card title"],
      ["booth.watchText", "Watch card text", "textarea"],
      ["booth.askTitle", "Questions card title"],
      ["booth.askText", "Questions card text", "textarea"],
      ["booth.alertsTitle", "Email card title"],
      ["booth.alertsText", "Email card text", "textarea"],
    ],
  },
  {
    id: "book",
    label: "Book",
    fields: [
      ["book.eyebrow", "Eyebrow"],
      ["book.titleBefore", "Title prefix"],
      ["book.titleEmphasis", "Title emphasis"],
      ["book.lede", "Book intro", "textarea"],
      ["book.pullQuote", "Pull quote", "textarea"],
      ["book.pullAttribution", "Pull quote attribution"],
      ["book.directTabLabel", "Direct tab"],
      ["book.directTabSub", "Direct tab sublabel"],
      ["book.directBadge", "Direct tab badge"],
      ["book.amazonTabLabel", "Amazon tab"],
      ["book.amazonTabSub", "Amazon tab sublabel"],
      ["book.directCta", "Direct button"],
      ["book.amazonCta", "Amazon button"],
      ["book.quickBuyCta", "Quick-buy button (mobile)"],
      ["book.directNote", "Direct note", "textarea"],
      ["book.amazonNote", "Amazon note", "textarea"],
      ["book.teaserTag", "Teaser tag"],
      ["book.teaserDate", "Teaser date"],
      ["book.teaserText", "Teaser text", "textarea"],
      ["book.quotes.0.text", "Quote 1 text", "textarea"],
      ["book.quotes.0.who", "Quote 1 person"],
      ["book.quotes.0.role", "Quote 1 role"],
      ["book.quotes.1.text", "Quote 2 text", "textarea"],
      ["book.quotes.1.who", "Quote 2 person"],
      ["book.quotes.1.role", "Quote 2 role"],
      ["book.quotes.2.text", "Quote 3 text", "textarea"],
      ["book.quotes.2.who", "Quote 3 person"],
      ["book.quotes.2.role", "Quote 3 role"],
      ["book.quotes.3.text", "Quote 4 text", "textarea"],
      ["book.quotes.3.who", "Quote 4 person"],
      ["book.quotes.3.role", "Quote 4 role"],
    ],
  },
  {
    id: "checkout",
    label: "Checkout",
    fields: [
      ["checkout.headerTag", "Header tag"],
      ["checkout.headerTitle", "Header title"],
      ["checkout.editionHeading", "Edition heading"],
      ["checkout.editionLede", "Edition intro", "textarea"],
      ["checkout.editions.standard.name", "Unsigned edition name"],
      ["checkout.editions.standard.sub", "Unsigned edition subtext", "textarea"],
      ["checkout.editions.signed.name", "Signed edition name"],
      ["checkout.editions.signed.sub", "Signed edition subtext", "textarea"],
      ["checkout.quantityLabel", "Quantity label"],
      ["checkout.quantityHelp", "Quantity help", "textarea"],
      ["checkout.addonTag", "Ball add-on tag"],
      ["checkout.addonName", "Ball add-on name"],
      ["checkout.addonSub", "Ball add-on subtext", "textarea"],
      ["checkout.personalizeHeading", "Personalization heading"],
      ["checkout.personalizeLedeSingle", "Personalization intro", "textarea"],
      ["checkout.personalizeLedeMultiple", "Multiple-copy personalization intro", "textarea"],
      ["checkout.unsignedNote", "Unsigned note", "textarea"],
      ["checkout.shippingHeading", "Shipping heading"],
      ["checkout.shippingLede", "Shipping intro", "textarea"],
      ["checkout.paymentHeading", "Payment heading"],
      ["checkout.paymentLede", "Payment intro", "textarea"],
    ],
  },
  {
    id: "memorabilia",
    label: "Collection",
    fields: [
      ["memorabilia.eyebrow", "Eyebrow"],
      ["memorabilia.titlePrefix", "Title prefix"],
      ["memorabilia.titleEmphasis", "Title emphasis"],
      ["memorabilia.titleSuffix", "Title suffix"],
      ["memorabilia.lede", "Intro", "textarea"],
      ...DEFAULT_SITE_CONTENT.memorabilia.items.flatMap((_, i) => [
        [`memorabilia.items.${i}.title`, `Item ${i + 1} title`],
        [`memorabilia.items.${i}.category`, `Item ${i + 1} category`],
        [`memorabilia.items.${i}.year`, `Item ${i + 1} year`],
        [`memorabilia.items.${i}.desc`, `Item ${i + 1} description`, "textarea"],
      ]),
    ],
  },
  {
    id: "gallery",
    label: "Gallery",
    fields: [
      ["gallery.eyebrow", "Eyebrow"],
      ["gallery.titlePrefix", "Title prefix"],
      ["gallery.titleEmphasis", "Title emphasis"],
      ["gallery.titleSuffix", "Title suffix"],
      ["gallery.lede", "Intro", "textarea"],
      ...DEFAULT_SITE_CONTENT.gallery.items.flatMap((item, i) => {
        const name = `${item.category === "Video" ? "Video" : "Photo"} ${i + 1}`;
        return [
          [`gallery.items.${i}.title`, `${name} title`],
          [`gallery.items.${i}.category`, `${name} category`],
          [`gallery.items.${i}.year`, `${name} year`],
          [`gallery.items.${i}.desc`, `${name} caption`, "textarea"],
        ];
      }),
    ],
  },
  {
    id: "watch",
    label: "Game Tape",
    fields: [
      ["watch.eyebrow", "Eyebrow"],
      ["watch.titlePrefix", "Title prefix"],
      ["watch.titleEmphasis", "Title emphasis"],
      ["watch.lede", "Intro", "textarea"],
      ["watch.ondeckNote", "On-deck note"],
    ],
  },
  {
    id: "about",
    label: "About",
    fields: [
      ["about.eyebrow", "Eyebrow"],
      ["about.titleLine1", "Title line 1"],
      ["about.titleLine2", "Title emphasis"],
      ["about.titleLine3", "Title line 2"],
      ["about.bio1", "Bio paragraph 1", "textarea"],
      ["about.bio2", "Bio paragraph 2", "textarea"],
      ["about.careerHeading", "Career heading"],
    ],
  },
  {
    id: "speaking",
    label: "Speaking",
    fields: [
      ["speaking.heading", "Heading"],
      ["speaking.subheading", "Subheading"],
      ["speaking.eyebrow", "Eyebrow"],
      ["speaking.headlineLine1", "Headline line 1"],
      ["speaking.headlineEmphasis", "Headline emphasis"],
      ["speaking.headlineLine2", "Headline line 2"],
      ["speaking.headlineLine3", "Headline line 3"],
      ["speaking.lede", "Intro", "textarea"],
      ["speaking.list.0.title", "List item 1 title"],
      ["speaking.list.0.text", "List item 1 text", "textarea"],
      ["speaking.list.1.title", "List item 2 title"],
      ["speaking.list.1.text", "List item 2 text", "textarea"],
      ["speaking.list.2.title", "List item 3 title"],
      ["speaking.list.2.text", "List item 3 text", "textarea"],
      ["speaking.cta", "Button"],
      ["speaking.note", "Button note"],
    ],
  },
  {
    id: "apply",
    label: "Sit Next To Me",
    fields: [
      ["apply.eyebrow", "Eyebrow"],
      ["apply.title", "Title prefix"],
      ["apply.titleEmphasis", "Title emphasis"],
      ["apply.lede", "Intro", "textarea"],
      ["apply.bullets.0", "Bullet 1", "textarea"],
      ["apply.bullets.1", "Bullet 2", "textarea"],
      ["apply.bullets.2", "Bullet 3", "textarea"],
      ["apply.bullets.3", "Bullet 4", "textarea"],
      ["apply.calloutTag", "Callout tag"],
      ["apply.calloutText", "Callout text", "textarea"],
      [
        "apply.status",
        "Applications (open shows the form, closed shows the off-season card)",
        "select",
        ["open", "closed"],
      ],
      ["apply.closed.tag", "Closed card tag"],
      ["apply.closed.title", "Closed card title"],
      ["apply.closed.titleEmphasis", "Closed card title emphasis"],
      ["apply.closed.text", "Closed card text", "textarea"],
      ["apply.closed.note", "Closed card note"],
      ["apply.formTag", "Form tag"],
      ["apply.finePrint", "Fine print"],
      ["apply.successEyebrow", "Success eyebrow"],
      ["apply.successTitle", "Success title"],
      ["apply.successTitleEmphasis", "Success title emphasis"],
      ["apply.successText", "Success text", "textarea"],
      ["apply.upcomingGames", "Upcoming home games, one per line (used when open)", "lines"],
    ],
  },
  {
    id: "mytake",
    label: "My Take",
    // The title/lede and the takes board are intentionally unrendered (Aug
    // 2026) — the section is just the weekly hot take, so the admin only
    // exposes the fields that still appear on the page. The hidden copy is
    // still in DEFAULT_SITE_CONTENT for an easy restore.
    fields: [
      ["mytake.eyebrow", "Eyebrow"],
      ["mytake.weeklyTag", "Weekly tag"],
      ["mytake.weekly.date", "Weekly date"],
      ["mytake.weekly.team", "Weekly team"],
      ["mytake.weekly.record", "Weekly record"],
      ["mytake.weekly.take", "Weekly take", "textarea"],
      ["mytake.weeklyFoot", "Weekly footer"],
    ],
  },
  {
    id: "health",
    label: "Health",
    fields: [
      ["health.eyebrow", "Eyebrow"],
      ["health.titleLine1", "Title line 1"],
      ["health.titleEmphasis", "Title emphasis"],
      ["health.lede", "Intro", "textarea"],
      ["health.pillars.0", "Pillar 1"],
      ["health.pillars.1", "Pillar 2"],
      ["health.pillars.2", "Pillar 3"],
      ["health.cta", "Button"],
      ["health.yearsLabel", "Years label"],
    ],
  },
  {
    id: "facebook",
    label: "Facebook",
    fields: [
      ["facebook.eyebrow", "Eyebrow"],
      ["facebook.titlePrefix", "Title prefix"],
      ["facebook.titleHandle", "Title handle"],
      ["facebook.followCta", "Follow button"],
      ["facebook.bottomCta", "Bottom button"],
    ],
  },
  {
    id: "contact",
    label: "Contact",
    fields: [
      ["contact.tag", "Tag"],
      ["contact.headingLine1", "Heading line 1"],
      ["contact.headingEmphasis", "Heading emphasis"],
      ["contact.lede", "Intro", "textarea"],
      ["contact.emailCta", "Email button"],
      ["contact.facebookCta", "Facebook button"],
      ["contact.pressCta", "Press kit button"],
      ["contact.autographCta", "Autograph button"],
    ],
  },
  {
    id: "footer",
    label: "Footer",
    fields: [
      ["footer.brand", "Brand"],
      ["footer.subbrand", "Subbrand"],
      ["footer.quote", "Quote", "textarea"],
      ["footer.copyright", "Copyright"],
      ["footer.credit", "Credit"],
    ],
  },
];

function isPlainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

export function mergeContent(base, override) {
  if (Array.isArray(base)) {
    if (!Array.isArray(override)) return base;
    if (base.every(isPlainObject) && override.every(isPlainObject)) {
      // When every element on both sides carries a stable `id`, merge by id
      // rather than by position. The persisted content file is merged over
      // these defaults, and a positional merge silently corrupts the array
      // the moment the code defaults are reordered or grow — inserting a nav
      // item shifts every later default onto the wrong override entry, which
      // is exactly how a stale persisted nav produced duplicate React keys.
      // Keying by id makes the code defaults authoritative for which items
      // exist and in what order, with the override supplying per-item edits.
      if (base.every((o) => "id" in o) && override.every((o) => "id" in o)) {
        const overrideById = new Map(override.map((o) => [o.id, o]));
        return base.map((item) =>
          overrideById.has(item.id) ? mergeContent(item, overrideById.get(item.id)) : item,
        );
      }
      const length = Math.max(base.length, override.length);
      return Array.from({ length }, (_, i) => mergeContent(base[i] || {}, override[i] || {}));
    }
    return override;
  }
  if (isPlainObject(base)) {
    const next = { ...base };
    if (!isPlainObject(override)) return next;
    for (const [key, value] of Object.entries(override)) {
      next[key] = key in base ? mergeContent(base[key], value) : value;
    }
    return next;
  }
  return override == null ? base : override;
}

export function normalizeSiteContent(content) {
  // `nav` is structural and owned entirely by the code — it is not exposed in
  // the admin editor (it has no entry in CONTENT_SECTIONS). A persisted
  // snapshot can still carry a stale copy, so drop it before merging to
  // guarantee the code defaults always define the navigation. Everything else
  // is editable copy and merges normally.
  const incoming =
    content && typeof content === "object" && !Array.isArray(content) ? { ...content } : {};
  delete incoming.nav;
  return mergeContent(DEFAULT_SITE_CONTENT, incoming);
}

const SiteContentContext = createContext(DEFAULT_SITE_CONTENT);

export function SiteContentProvider({ children }) {
  const [remote, setRemote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/site-content.json?ts=${Date.now()}`, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => {
        if (!cancelled) setRemote(data && typeof data === "object" ? data : {});
      })
      .catch(() => {
        if (!cancelled) setRemote({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const content = useMemo(() => normalizeSiteContent(remote), [remote]);
  return React.createElement(SiteContentContext.Provider, { value: content }, children);
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
